import { classNames } from "@/utils/classnames";
import { ComponentProps, ForwardedRef, PropsWithChildren, forwardRef } from "react";

export type Rotundity = "sm" | "md" | "lg" | "xl";

export interface RoundCardProps {
  className?: string;
  roundness?: Rotundity;
}

const rotundityClass = (rotundity: Rotundity) => {
  switch (rotundity) {
    case "sm":
      return "rounded-sm";
    case "md":
      return "rounded-md";
    case "lg":
      return "rounded-lg";
    case "xl":
      return "rounded-xl";
  }
};

export const RoundCard = forwardRef<HTMLDivElement, PropsWithChildren<RoundCardProps>>(
  ({ children, className, roundness = "xl" }, ref) => {
    return (
      <div
        data-page-ui=""
        className={classNames(
          `py-3 px-5 border overflow-hidden`,
          rotundityClass(roundness),
          className,
        )}
        ref={ref}
      >
        {children}
      </div>
    );
  },
);

RoundCard.displayName = "RoundCard";

export const Card = forwardRef(function Card(
  {
    children,
    className,
    size = "md",
    ...rest
  }: ComponentProps<"div"> & { size?: "sm" | "md" | "lg" },
  ref: ForwardedRef<HTMLElement>,
) {
  return (
    <section
      data-page-ui=""
      ref={ref}
      className={classNames({ sm: "card-sm", md: "card-md", lg: "card-lg" }[size], className)}
      {...rest}
    >
      {children}
    </section>
  );
});

export const Well = forwardRef(function Well(
  {
    children,
    className,
    deep,
    deeper,
    ...rest
  }: ComponentProps<"div"> & { deep?: boolean; deeper?: boolean },
  ref: ForwardedRef<HTMLElement>,
) {
  return (
    <section
      data-page-ui=""
      ref={ref}
      className={classNames("well", deeper && "well-deeper", deep && "well-deep", className)}
      {...rest}
    >
      {children}
    </section>
  );
});

export const Section = forwardRef(function Section(
  {
    children,
    className,
    size = "md",
    ...rest
  }: ComponentProps<"div"> & { size?: "sm" | "md" | "lg" },
  ref: ForwardedRef<HTMLElement>,
) {
  const s = { sm: "rounded", md: "rounded-md", lg: "rounded-md" }[size];
  return (
    <section data-page-ui="" ref={ref} className={classNames("border p-4", s, className)} {...rest}>
      {children}
    </section>
  );
});
